import fromUnixTime from 'date-fns/fromUnixTime';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { EventType } from '@lib/types';
import { StripeProvider, TappayProvider } from '@providers/PaymentProvider';
import { getGoogleCalendarUrl } from '@lib/utils';

import Modal from '../modal';

import OrderBlock from './OrderBlock';
import TicketBlock from './TicketBlock';
import getSelectedTicketPrice from './utils/getSelectedTicketPrice';

type Props = {
  event: EventType;
  disabled?: boolean;
  buttonProps?: ButtonProps;
  onSuccess?: () => void;
};

function GetTicket({ event, disabled, buttonProps, onSuccess }: Props) {
  const toast = useToast();
  const { t } = useTranslation(['common']);
  const [purchaseState, setPurchaseState] = useState<'success' | 'purchasing'>(
    'purchasing'
  );
  const [errorMsg, setErrorMsg] = useState('');
  const [redeemCode, setRedeemCode] = useState(''); // For google calendar event purpose

  const [quantity, setQuantity] = useState(1);
  const [selectedTicketId, setSelectedTicketId] = useState(
    event?.tickets?.[0].id
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const selectedTicket = event.tickets.find(
    ({ id }) => selectedTicketId === id
  );

  const checkoutAmount =
    getSelectedTicketPrice({ selectedTicket, quantity }) * quantity;

  const stripeAccountId = event.stripe?.accountId;

  const stripePromise = useMemo(() => {
    if (!stripeAccountId) {
      return;
    }

    return loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY || '', {
      stripeAccount: stripeAccountId,
    });
  }, [stripeAccountId]);

  const handleSuccess = (redeemCode: string) => {
    setRedeemCode(redeemCode);
    setPurchaseState('success');
    setErrorMsg('');

    if (onSuccess) {
      onSuccess();
    }
  };
  const handleError = useCallback(
    (e: string) => {
      setErrorMsg(e);
      toast({ title: e, status: 'error' });
    },
    [toast]
  );
  return (
    <>
      <Button
        w="100%"
        colorScheme="brand"
        onClick={onOpen}
        disabled={event.tickets.length === 0 || disabled}
        {...buttonProps}
      >
        {t('Get Ticket')}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setPurchaseState('purchasing');
          onClose();
        }}
        title={t('Purchase Ticket')}
      >
        <Heading
          as="h2"
          fontSize="2xl"
          borderBottom="1px"
          borderColor="gray.100"
          pb={8}
        >
          {event.name}
        </Heading>
        {errorMsg && (
          <Heading as="h6" fontSize="md" color="red.500" p={4}>
            {errorMsg}
          </Heading>
        )}
        <Flex
          width={{ base: '100%', md: 'clamp(3xl, 80%, 4xl)' }}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          {purchaseState === 'success' ? (
            <Box p={8} width="full">
              <Heading fontSize="4xl" color="green.500" textAlign="center">
                {t('Success')} 🥳
              </Heading>
              <Heading
                textAlign="center"
                fontSize="md"
                color="gray.500"
                mt={8}
                fontWeight="normal"
              >
                {t(
                  'Please check your email for the ticket, look forward to seeing you at the event'
                )}
              </Heading>
              <Button
                as="a"
                width="full"
                mt={4}
                colorScheme="brand"
                href={getGoogleCalendarUrl({
                  text: event.name,
                  startDate: fromUnixTime(event.startedAt),
                  endDate: fromUnixTime(event.endedAt),
                  description: t(
                    'Welcome to the event, click the link: {{link}} to enter the room when event start',
                    {
                      link: `https://nutshell.live/s/${event.id}?t=${redeemCode}`,
                      nsSeparator: '|',
                    }
                  ),
                })}
                target="blank"
              >
                {t('Add to Google Calendar')}
              </Button>
            </Box>
          ) : (
            <>
              {stripeAccountId &&
                stripePromise &&
                // <Elements stripe={stripePromise}>
                //   <StripeProvider>
                //     <TicketBlock
                //       tickets={event.tickets}
                //       selectedTicket={selectedTicket}
                //       checkoutAmount={checkoutAmount}
                //       onSuccess={handleSuccess}
                //       onError={handleError}
                //       event={event}
                //       onTicketIdChange={setSelectedTicketId}
                //       onQuantityChange={setQuantity}
                //     />
                //   </StripeProvider>
                // </Elements>
                null}
              {!stripeAccountId && (
                <TappayProvider>
                  <TicketBlock
                    tickets={event.tickets}
                    selectedTicket={selectedTicket}
                    checkoutAmount={checkoutAmount}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    event={event}
                    onTicketIdChange={setSelectedTicketId}
                    onQuantityChange={setQuantity}
                  />
                </TappayProvider>
              )}

              <OrderBlock
                selectedTicket={selectedTicket}
                quantity={quantity}
                checkoutAmount={checkoutAmount}
              />
            </>
          )}
        </Flex>
      </Modal>
    </>
  );
}

export default GetTicket;
